import type { AuthError, Session } from '@supabase/supabase-js';
import { supabase } from '../config/supabase';
import { phoneToAuthEmail } from './roles';
import { formatPhoneToInternational } from '../utils/validators';

export interface SignInResult {
  session: Session | null;
  error: AuthError | null;
}

export async function signInWithEmailPassword(
  email: string,
  password: string
): Promise<SignInResult> {
  const { data, error } = await supabase.auth.signInWithPassword({ email, password });
  return { session: data.session, error };
}

export async function signInWithPhonePassword(
  phone: string,
  password: string
): Promise<SignInResult> {
  const normalizedPhone = formatPhoneToInternational(phone.trim());
  const email = phoneToAuthEmail(normalizedPhone);
  return signInWithEmailPassword(email, password);
}

export type AuthErrorCode =
  | 'invalid_credentials'
  | 'email_not_confirmed'
  | 'too_many_requests'
  | 'raw';

export function getAuthErrorCode(
  error: AuthError | Error,
  mode: 'email' | 'phone' = 'email'
): AuthErrorCode {
  const message = error.message;
  void mode;

  if (message.includes('Invalid login credentials')) return 'invalid_credentials';
  if (message.includes('Email not confirmed')) return 'email_not_confirmed';
  if (message.includes('Too many requests')) return 'too_many_requests';

  return 'raw';
}

export function getFriendlyAuthError(error: AuthError | Error, mode: 'email' | 'phone' = 'email'): string {
  switch (getAuthErrorCode(error, mode)) {
    case 'invalid_credentials':
      return mode === 'phone'
        ? 'Invalid phone number or password'
        : 'Invalid email or password';
    case 'email_not_confirmed':
      return 'Account not verified. Please contact support.';
    case 'too_many_requests':
      return 'Too many attempts. Please try again later.';
    case 'raw':
      return error.message;
  }
}
